import { SearchOutlined } from '@ant-design/icons';
import { Empty, Flex, Input, Spin, Tag, Tree } from 'antd';
import type { TreeDataNode, TreeProps } from 'antd';
import {
  ComponentRef,
  Key,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { RecipientCompany } from '@hooks-api';

import {
  EMPTY_EXCLUSIONS,
  Exclusions,
  computeCheckedKeys,
  memberKey,
  toggleCompany,
  toggleMember,
} from './selection';
import * as S from './styled';

interface Props {
  companies: RecipientCompany[];
  exclusions: Exclusions;
  onChange: (next: Exclusions) => void;
  isLoading?: boolean;
}

/** Imperative handle so the coverage list can jump the tree to a company. */
export interface RecipientTreeHandle {
  focusCompany: (companyId: string) => void;
}

/** Fixed row height needed by the tree's virtual scroller (large lists). */
const TREE_HEIGHT = 440;

const matches = (term: string, ...fields: string[]) =>
  fields.some((f) => f.toLowerCase().includes(term));

const RecipientTree = forwardRef<RecipientTreeHandle, Props>(
  ({ companies, exclusions, onChange, isLoading }, ref) => {
    const [search, setSearch] = useState('');
    const term = search.trim().toLowerCase();
    // Company to scroll to once the (search-cleared) tree has re-rendered.
    const [pendingFocus, setPendingFocus] = useState<string | null>(null);
    // Company to flash briefly after a coverage-list jump.
    const [highlighted, setHighlighted] = useState<string | null>(null);
    // User-toggled expansion (null = follow the computed default of all-expanded).
    const [manualExpanded, setManualExpanded] = useState<Key[] | null>(null);
    const treeRef = useRef<ComponentRef<typeof Tree>>(null);

    // Emails present in more than one company — surfaced so the marketer knows a
    // person is shared and (once deduped) receives a single email.
    const sharedEmails = useMemo(() => {
      const counts = new Map<string, number>();
      companies.forEach((c) =>
        c.members.forEach((m) => counts.set(m.email.toLowerCase(), (counts.get(m.email.toLowerCase()) ?? 0) + 1))
      );
      return new Set([...counts.entries()].filter(([, n]) => n > 1).map(([email]) => email));
    }, [companies]);

    const byId = useMemo(() => new Map(companies.map((c) => [c.companyId, c])), [companies]);

    const treeData = useMemo<TreeDataNode[]>(() => companies
        .map((company) => {
          const companyMatches = matches(term, company.companyName);
          const members = company.members.filter(
            (m) => !term || companyMatches || matches(term, m.name, m.email)
          );
          if (term && !companyMatches && members.length === 0) return null;

          const selectedCount = company.members.filter(
            (m) => !exclusions.companyIds.includes(company.companyId) && !exclusions.userIds.includes(m.userId)
          ).length;

          return {
            key: company.companyId,
            title: (
              <S.TitleRow
                align="center"
                justify="space-between"
                gap={8}
                highlighted={highlighted === company.companyId}
              >
                <S.CompanyTitle>{company.companyName}</S.CompanyTitle>
                <Tag bordered={false} color={selectedCount ? 'blue' : 'default'}>
                  {selectedCount}/{company.members.length}
                </Tag>
              </S.TitleRow>
            ),
            children: members.map((m) => ({
              key: memberKey(company.companyId, m.userId),
              title: (
                <S.TitleRow align="center" justify="space-between" gap={8}>
                  <Flex align="center" gap={8}>
                    <S.MemberName>{m.name || m.email}</S.MemberName>
                    {sharedEmails.has(m.email.toLowerCase()) && (
                      <Tag bordered={false} color="orange">
                        also in another company
                      </Tag>
                    )}
                  </Flex>
                  <S.MemberEmail>{m.email}</S.MemberEmail>
                </S.TitleRow>
              ),
            })),
          } as TreeDataNode;
        })
        .filter((n): n is TreeDataNode => n !== null), [companies, exclusions, term, sharedEmails, highlighted]);

    const checkedKeys = useMemo(() => computeCheckedKeys(companies, exclusions), [companies, exclusions]);

    // While searching, expand matching companies. Otherwise default to everything
    // expanded (members visible for review) but let the user collapse/expand: once
    // they toggle, honor their selection via `onExpand` below.
    const expandedKeys = useMemo<Key[]>(
      () => (term ? treeData.map((n) => n.key) : manualExpanded ?? companies.map((c) => c.companyId)),
      [term, treeData, companies, manualExpanded]
    );

    useImperativeHandle(ref, () => ({
      focusCompany: (companyId: string) => {
        // Clear any active search so the company is guaranteed to be in view,
        // then defer the scroll to the next render (below).
        setSearch('');
        setPendingFocus(companyId);
      },
    }));

    // Scroll to the requested company once the search-cleared tree has rendered.
    useEffect(() => {
      if (!pendingFocus) return;
      treeRef.current?.scrollTo({ key: pendingFocus, align: 'top' });
      setHighlighted(pendingFocus);
      setPendingFocus(null);
    }, [pendingFocus, treeData]);

    // Clear the highlight after a short flash.
    useEffect(() => {
      if (!highlighted) return;
      const id = setTimeout(() => setHighlighted(null), 1600);
      return () => clearTimeout(id);
    }, [highlighted]);

    const handleCheck: NonNullable<TreeProps['onCheck']> = (_checked, info) => {
      const key = String(info.node.key);
      if (key.includes(':')) {
        const [companyId, userId] = key.split(':');
        const company = byId.get(companyId);
        if (company) onChange(toggleMember(exclusions, company, userId, info.checked));
        return;
      }
      const company = byId.get(key);
      if (company) onChange(toggleCompany(exclusions, company, info.checked));
    };

    const allCompanyIds = companies.map((c) => c.companyId);

    return (
      <div>
        <S.Toolbar align="center" gap={8}>
          <Input
            allowClear
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search company or member…"
            prefix={<SearchOutlined />}
          />
          <S.LinkButton
            type="button"
            onClick={() => onChange(EMPTY_EXCLUSIONS)}
            disabled={exclusions.companyIds.length === 0 && exclusions.userIds.length === 0}
          >
            Select all
          </S.LinkButton>
          <S.LinkButton type="button" onClick={() => onChange({ companyIds: allCompanyIds, userIds: [] })}>
            Clear
          </S.LinkButton>
        </S.Toolbar>

        <S.TreeWrap>
          <Spin spinning={!!isLoading}>
            {treeData.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={isLoading ? 'Loading recipients…' : term ? 'No matches' : 'No eligible recipients'}
              />
            ) : (
              <Tree
                ref={treeRef}
                checkable
                selectable={false}
                height={TREE_HEIGHT}
                treeData={treeData}
                checkedKeys={checkedKeys}
                expandedKeys={expandedKeys}
                onExpand={(keys) => setManualExpanded(keys)}
                onCheck={handleCheck}
              />
            )}
          </Spin>
        </S.TreeWrap>
      </div>
    );
  }
);

RecipientTree.displayName = 'RecipientTree';

export default RecipientTree;
